import React, { useState, useEffect, useRef, useCallback } from 'react';
import './Questionnaires.css';
import apiService from '../../services/api';
import { useAuth } from '../../auth/AuthContext';
import { Link, useSearchParams } from 'react-router-dom';

const QUESTIONNAIRE_TYPE = 'intake';
const AUTO_SAVE_DELAY_MS = 3000;

const PHASE_1_REQUIRED_FIELDS = [
  'sex_at_birth',
  'date_of_birth',
  'height_ft',
  'height_in',
  'weight_value',
  'weight_unit',
  'primary_objectives',
];

function isPhase1Complete(responses) {
  if (!responses || typeof responses !== 'object') return false;
  return PHASE_1_REQUIRED_FIELDS.every((field) => {
    const val = responses[field];
    if (val === undefined || val === null || val === '') return false;
    if (Array.isArray(val) && val.length === 0) return false;
    return true;
  });
}

/** Pure helper: is a field visible given current responses? */
function shouldShowFieldPure(responses, fieldConfig) {
  const showIf = fieldConfig?.show_if;
  if (!showIf) return true;
  const val = responses[showIf.field];
  if (Object.prototype.hasOwnProperty.call(showIf, 'equals')) return val === showIf.equals;
  if (Array.isArray(showIf.not_in)) return !showIf.not_in.includes(val);
  if (Array.isArray(showIf.in)) return showIf.in.includes(val);
  if (Object.prototype.hasOwnProperty.call(showIf, 'gt')) return typeof val === 'number' && val > showIf.gt;
  return true;
}

/** Pure helper: is a phase visible given responses? */
function isPhaseVisiblePure(responses, phase) {
  if (!phase?.show_if) return true;
  const val = responses[phase.show_if.field];
  if (phase.show_if.equals !== undefined) return val === phase.show_if.equals;
  return true;
}

/** True when every visible phase is complete (for dashboard). Uses draft responses + schema phases. */
export function areAllPhasesComplete(responses, phases) {
  if (!responses || typeof responses !== 'object' || !Array.isArray(phases) || phases.length === 0) return false;
  for (let i = 0; i < phases.length; i++) {
    const phase = phases[i];
    if (!isPhaseVisiblePure(responses, phase)) continue;
    if (phase.required_phase) {
      if (!isPhase1Complete(responses)) return false;
      continue;
    }
    if (!phase.fields || typeof phase.fields !== 'object') continue;
    const hasRequired = Object.values(phase.fields).some((f) => f.required === true);
    if (hasRequired) {
      for (const [fieldName, fieldConfig] of Object.entries(phase.fields)) {
        if (!fieldConfig.required || fieldConfig.type === 'section_header') continue;
        if (!shouldShowFieldPure(responses, fieldConfig)) continue;
        const val = responses[fieldName];
        if (val === undefined || val === null || val === '' || (Array.isArray(val) && val.length === 0)) return false;
      }
    } else {
      for (const [fieldName, fieldConfig] of Object.entries(phase.fields)) {
        if (fieldConfig.type === 'section_header') continue;
        if (!shouldShowFieldPure(responses, fieldConfig)) continue;
        const val = responses[fieldName];
        if (val === undefined || val === null || val === '' || (Array.isArray(val) && val.length === 0)) return false;
      }
    }
  }
  return true;
}

function Questionnaires() {
  const { user, refreshMe } = useAuth();
  const [searchParams] = useSearchParams();
  const fromOnboarding = searchParams.get('from') === 'onboarding';
  const [schemas, setSchemas] = useState(null);
  const [formData, setFormData] = useState({});
  const [currentPhase, setCurrentPhase] = useState(0);
  const [message, setMessage] = useState('');
  const [loadingSchemas, setLoadingSchemas] = useState(true);
  const [loadingDraft, setLoadingDraft] = useState(true);
  const [authError, setAuthError] = useState(false);
  const [pullingWearable, setPullingWearable] = useState(false);
  const [autoSaveStatus, setAutoSaveStatus] = useState('idle');
  const [researchOptInSaving, setResearchOptInSaving] = useState(false);

  const optedInToResearch = user?.opted_in_to_research !== false;

  const handleResearchOptInToggle = async () => {
    const next = !optedInToResearch;
    setResearchOptInSaving(true);
    try {
      await apiService.users.updateResearchOptedIn(next);
      await refreshMe();
    } catch (e) {
      console.error('Failed to update research opt-in:', e);
    } finally {
      setResearchOptInSaving(false);
    }
  };

  const autoSaveTimer = useRef(null);
  const formDataRef = useRef(formData);
  const currentPhaseRef = useRef(currentPhase);
  const hasDraftLoaded = useRef(false);

  formDataRef.current = formData;
  currentPhaseRef.current = currentPhase;

  const doAutoSave = useCallback(async () => {
    const data = formDataRef.current;
    if (!data || Object.keys(data).length === 0) return;
    setAutoSaveStatus('saving');
    try {
      await apiService.questionnaires.saveDraft({
        questionnaire_type: QUESTIONNAIRE_TYPE,
        responses: data,
        current_phase: currentPhaseRef.current,
      });
      setAutoSaveStatus('saved');
    } catch {
      setAutoSaveStatus('error');
    }
  }, []);

  const scheduleAutoSave = useCallback(() => {
    if (!hasDraftLoaded.current) return;
    if (autoSaveTimer.current) clearTimeout(autoSaveTimer.current);
    setAutoSaveStatus('idle');
    autoSaveTimer.current = setTimeout(doAutoSave, AUTO_SAVE_DELAY_MS);
  }, [doAutoSave]);

  useEffect(() => {
    return () => { if (autoSaveTimer.current) clearTimeout(autoSaveTimer.current); };
  }, []);

  const fetchSchemas = async () => {
    try {
      setLoadingSchemas(true);
      setAuthError(false);
      const response = await apiService.questionnaires.getTypes();
      setSchemas(response.data.types);
    } catch (error) {
      console.error('Error fetching schemas:', error);
      if (error?.response?.status === 401) {
        setAuthError(true);
        setMessage('Please log in to access the questionnaire.');
      } else {
        setMessage('Error loading questionnaire');
      }
    } finally {
      setLoadingSchemas(false);
    }
  };

  const loadDraft = useCallback(async () => {
    try {
      const res = await apiService.questionnaires.getDraft(QUESTIONNAIRE_TYPE);
      const draft = res.data?.draft;
      if (draft && draft.responses && Object.keys(draft.responses).length > 0) {
        setFormData(draft.responses);
        setCurrentPhase(draft.current_phase ?? 0);
      }
    } catch {
      // no draft to load
    } finally {
      setLoadingDraft(false);
      hasDraftLoaded.current = true;
    }
  }, []);

  useEffect(() => {
    fetchSchemas();
    loadDraft();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const intakeSchema = schemas && schemas[QUESTIONNAIRE_TYPE];
  const phases = intakeSchema?.phases ?? [];
  const totalPhases = phases.length;

  // --- Phase visibility (for conditional phases like Reproductive History) ---
  const isPhaseVisible = (phaseIdx) => {
    const phase = phases[phaseIdx];
    if (!phase?.show_if) return true;
    const val = formData[phase.show_if.field];
    if (phase.show_if.equals !== undefined) return val === phase.show_if.equals;
    return true;
  };

  const visiblePhaseIndices = [];
  for (let i = 0; i < totalPhases; i++) {
    if (isPhaseVisible(i)) visiblePhaseIndices.push(i);
  }

  // If current phase became hidden, snap to nearest visible
  const effectivePhase = visiblePhaseIndices.includes(currentPhase)
    ? currentPhase
    : (visiblePhaseIndices[0] ?? 0);

  useEffect(() => {
    if (effectivePhase !== currentPhase) {
      setCurrentPhase(effectivePhase);
    }
  }, [effectivePhase, currentPhase]);

  const currentPhaseData = phases[effectivePhase] || null;
  const currentVisibleIdx = visiblePhaseIndices.indexOf(effectivePhase);
  const isLastVisiblePhase = currentVisibleIdx === visiblePhaseIndices.length - 1;
  const isFirstVisiblePhase = currentVisibleIdx === 0;

  /* Show all phase circles even during onboarding (no single-step stepper) */
  const stepperPhaseIndices = visiblePhaseIndices;
  const requiredIntakeComplete = isPhase1Complete(formData);

  // --- Field visibility ---
  const shouldShowField = (fieldName, fieldConfig) => {
    const showIf = fieldConfig.show_if;
    if (!showIf) return true;
    const val = formData[showIf.field];
    if (Object.prototype.hasOwnProperty.call(showIf, 'equals')) {
      return val === showIf.equals;
    }
    if (Array.isArray(showIf.not_in)) {
      return !showIf.not_in.includes(val);
    }
    if (Array.isArray(showIf.in)) {
      return showIf.in.includes(val);
    }
    if (Object.prototype.hasOwnProperty.call(showIf, 'gt')) {
      return typeof val === 'number' && val > showIf.gt;
    }
    return true;
  };

  // --- Phase complete: required phase = required fields; optional phase = all visible fields filled ---
  const isPhaseComplete = (phaseIdx) => {
    const phase = phases[phaseIdx];
    if (!phase?.fields) return false;
    const hasRequired = Object.values(phase.fields).some((f) => f.required === true);
    for (const [fieldName, fieldConfig] of Object.entries(phase.fields)) {
      if (fieldConfig.type === 'section_header') continue;
      if (!shouldShowField(fieldName, fieldConfig)) continue;
      if (hasRequired && !fieldConfig.required) continue;
      const val = formData[fieldName];
      if (val === undefined || val === null || val === '' || (Array.isArray(val) && val.length === 0)) {
        return false;
      }
    }
    return true;
  };

  // --- Required field validation (Phase 1) ---
  const validateRequiredFields = () => {
    if (!currentPhaseData?.required_phase) return true;
    const missing = [];
    for (const [fieldName, fieldConfig] of Object.entries(currentPhaseData.fields)) {
      if (!fieldConfig.required) continue;
      if (fieldConfig.type === 'section_header') continue;
      if (!shouldShowField(fieldName, fieldConfig)) continue;
      const val = formData[fieldName];
      if (val === undefined || val === null || val === '' || (Array.isArray(val) && val.length === 0)) {
        missing.push(fieldConfig.label);
      }
    }
    if (missing.length > 0) {
      setMessage(`Please fill in required fields: ${missing.join(', ')}`);
      return false;
    }
    return true;
  };

  const handleInputChange = (fieldName, value) => {
    setFormData(prev => ({ ...prev, [fieldName]: value }));
    scheduleAutoSave();
  };

  const handleArrayToggle = (fieldName, option) => {
    setFormData(prev => {
      const arr = Array.isArray(prev[fieldName]) ? [...prev[fieldName]] : [];
      const idx = arr.indexOf(option);
      if (idx >= 0) arr.splice(idx, 1);
      else arr.push(option);
      return { ...prev, [fieldName]: arr };
    });
    scheduleAutoSave();
  };

  const handlePullWearable = async () => {
    setPullingWearable(true);
    setMessage('');
    try {
      const end = new Date().toISOString().slice(0, 10);
      const start = new Date(Date.now() - 7 * 86400000).toISOString().slice(0, 10);
      const pulled = [];

      // Resting heart rate via timeseries (not raw heart_rate)
      try {
        const tsRes = await apiService.wearables.getTimeseries(
          `${start}T00:00:00Z`, `${end}T23:59:59Z`, ['resting_heart_rate'], 100, 'raw'
        );
        const hrPoints = tsRes.data?.data || [];
        if (hrPoints.length > 0) {
          const values = hrPoints.map(d => d.value || d.bpm || 0).filter(v => v > 0);
          if (values.length > 0) {
            const avg = Math.round(values.reduce((s, v) => s + v, 0) / values.length);
            handleInputChange('resting_heart_rate', avg);
            pulled.push('resting heart rate');
          }
        }
      } catch (e) { /* resting HR not available */ }

      // Sleep via sessions (not broken summary endpoint)
      try {
        const sleepRes = await apiService.wearables.getSleepSessions(start, end, 50);
        const sleepData = sleepRes.data?.data || sleepRes.data || [];
        if (sleepData.length > 0) {
          const durations = sleepData
            .map(d => (d.duration_seconds || d.total_sleep_time_seconds || 0) / 3600)
            .filter(v => v > 0);
          if (durations.length > 0) {
            const avg = Math.round(durations.reduce((s, v) => s + v, 0) / durations.length * 10) / 10;
            handleInputChange('sleep_hours', avg);
            pulled.push('sleep hours');
          }
        }
      } catch (e) { /* sleep not available */ }

      if (pulled.length > 0) {
        setMessage(`Pulled from wearable: ${pulled.join(', ')}`);
      } else {
        setMessage('No wearable data found. Make sure a device is connected.');
      }
    } catch (e) {
      setMessage('Could not connect to wearable data. Make sure a device is connected.');
    } finally {
      setPullingWearable(false);
    }
  };

  // --- Field rendering ---
  const renderField = (fieldName, fieldConfig) => {
    if (!shouldShowField(fieldName, fieldConfig)) return null;
    const { type, label, description, options, min, max } = fieldConfig;

    // Section headers
    if (type === 'section_header') {
      return (
        <div key={fieldName} className="form-section-header">
          <h3>{label}</h3>
          {fieldConfig.data_source === 'wearable' && (
            <button
              type="button"
              className="btn btn-wearable"
              onClick={handlePullWearable}
              disabled={pullingWearable}
            >
              {pullingWearable ? 'Pulling...' : 'Pull from wearable data'}
            </button>
          )}
          {fieldConfig.data_source === 'lab' && (
            <p className="form-note">
              These fields can be auto-filled from your uploaded lab results.
              <Link to="/lab-results" style={{ marginLeft: '0.5rem' }}>Upload labs</Link>
            </p>
          )}
        </div>
      );
    }

    const value = formData[fieldName];
    const labelEl = (
      <label key={`${fieldName}-label`}>
        {label}
        {fieldConfig.required && <span className="required"> *</span>}
      </label>
    );
    const descEl = description ? <p className="field-description">{description}</p> : null;

    if (type === 'boolean') {
      return (
        <div key={fieldName} className="form-field form-field-boolean">
          {labelEl}
          {descEl}
          <div className="boolean-options">
            <label className="radio-option">
              <input
                type="radio"
                name={fieldName}
                checked={value === true}
                onChange={() => handleInputChange(fieldName, true)}
              />
              Yes
            </label>
            <label className="radio-option">
              <input
                type="radio"
                name={fieldName}
                checked={value === false}
                onChange={() => handleInputChange(fieldName, false)}
              />
              No
            </label>
          </div>
        </div>
      );
    }

    if (type === 'string' && options) {
      return (
        <div key={fieldName} className="form-field">
          {labelEl}
          {descEl}
          <select
            value={value ?? ''}
            onChange={(e) => handleInputChange(fieldName, e.target.value || undefined)}
          >
            <option value="">Select...</option>
            {options.map(opt => (
              <option key={opt} value={opt}>
                {opt.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
              </option>
            ))}
          </select>
        </div>
      );
    }

    if (type === 'array' && options) {
      const selected = Array.isArray(value) ? value : [];
      return (
        <div key={fieldName} className="form-field form-field-array">
          {labelEl}
          {descEl}
          <div className="checkbox-group">
            {options.map(opt => (
              <label key={opt} className="checkbox-option">
                <input
                  type="checkbox"
                  checked={selected.includes(opt)}
                  onChange={() => handleArrayToggle(fieldName, opt)}
                />
                {opt}
              </label>
            ))}
          </div>
        </div>
      );
    }

    if (type === 'date') {
      const handleDateChange = (e) => {
        const prev = value ?? '';
        const raw = e.target.value;

        // Allow clearing
        if (raw === '') { handleInputChange(fieldName, ''); return; }

        // Strip everything except digits and slashes typed by the user
        const digits = raw.replace(/\D/g, '').slice(0, 8);

        // Auto-insert slashes: MM/DD/YYYY
        let formatted = '';
        for (let i = 0; i < digits.length; i++) {
          if (i === 2 || i === 4) formatted += '/';
          formatted += digits[i];
        }

        // If the user deleted a slash, shorten cleanly
        if (raw.length < prev.length) {
          formatted = raw.replace(/[^0-9/]/g, '');
          // Remove trailing slash left behind after backspace
          if (formatted.endsWith('/') && !prev.endsWith('/')) {
            formatted = formatted.slice(0, -1);
          }
        }

        handleInputChange(fieldName, formatted);
      };

      return (
        <div key={fieldName} className="form-field">
          {labelEl}
          {descEl}
          <input
            type="text"
            inputMode="numeric"
            placeholder="MM/DD/YYYY"
            maxLength={10}
            value={value ?? ''}
            onChange={handleDateChange}
          />
        </div>
      );
    }

    if (type === 'number') {
      const isScale = min === 1 && max === 10;
      return (
        <div key={fieldName} className="form-field">
          {labelEl}
          {descEl}
          {isScale ? (
            <div className="scale-input">
              <input
                type="range"
                min={min}
                max={max}
                value={value ?? 5}
                onChange={(e) => handleInputChange(fieldName, parseInt(e.target.value, 10))}
              />
              <span className="scale-value">{value ?? 5}</span>
            </div>
          ) : (
            <input
              type="number"
              min={min}
              max={max}
              value={value ?? ''}
              onChange={(e) => {
                const v = e.target.value === '' ? undefined : parseFloat(e.target.value);
                handleInputChange(fieldName, v);
              }}
              placeholder={description}
            />
          )}
        </div>
      );
    }

    if (fieldConfig.multiline) {
      return (
        <div key={fieldName} className="form-field">
          {labelEl}
          {descEl}
          <textarea
            value={value ?? ''}
            onChange={(e) => handleInputChange(fieldName, e.target.value)}
            placeholder={description}
            rows={4}
          />
        </div>
      );
    }

    return (
      <div key={fieldName} className="form-field">
        {labelEl}
        {descEl}
        <input
          type="text"
          value={value ?? ''}
          onChange={(e) => handleInputChange(fieldName, e.target.value)}
          placeholder={description}
        />
      </div>
    );
  };

  // --- Navigation ---
  const handlePrev = () => {
    setMessage('');
    const prevIdx = currentVisibleIdx - 1;
    if (prevIdx >= 0) {
      setCurrentPhase(visiblePhaseIndices[prevIdx]);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const handleNext = async (e) => {
    if (e) e.preventDefault();
    setMessage('');
    if (!validateRequiredFields()) return;
    if (fromOnboarding && currentPhaseData?.required_phase) {
      try {
        await apiService.users.completeOnboarding();
        await refreshMe();
      } catch (err) {
        console.error('Failed to complete onboarding:', err);
      }
    }
    const nextIdx = currentVisibleIdx + 1;
    if (nextIdx < visiblePhaseIndices.length) {
      setCurrentPhase(visiblePhaseIndices[nextIdx]);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const autoSaveLabel =
    autoSaveStatus === 'saving' ? 'Saving...' :
    autoSaveStatus === 'saved' ? 'Saved' :
    autoSaveStatus === 'error' ? 'Auto-save failed' : null;

  if (loadingSchemas || loadingDraft) {
    return (
      <div className="page">
        <p>Loading questionnaire...</p>
      </div>
    );
  }

  if (authError) {
    return (
      <div className="page">
        <p>Please log in to access the questionnaire.</p>
        <p>
          <Link to="/login">Go to login</Link>
        </p>
      </div>
    );
  }

  if (!intakeSchema) {
    return (
      <div className="page">
        <p>No questionnaire available.</p>
        {message && <p style={{ color: '#6b7280' }}>{message}</p>}
      </div>
    );
  }

  return (
    <div className="page questionnaires-page">
      {fromOnboarding && (
        <div className="onboarding-banner">
          <span className="onboarding-banner-text">You're setting up your account.</span>
          {requiredIntakeComplete && (
            <Link to="/onboarding" className="onboarding-banner-button">Add other data</Link>
          )}
        </div>
      )}
      <h1>Health Intake Questionnaire</h1>

      <div className="research-opt-in-block">
        <label className="research-opt-in-row research-opt-in-checkbox-wrap">
          <input
            type="checkbox"
            checked={optedInToResearch}
            onChange={() => !researchOptInSaving && handleResearchOptInToggle()}
            disabled={researchOptInSaving}
            aria-label={optedInToResearch ? 'Opted in to research' : 'Opted out of research'}
            className="research-opt-in-checkbox"
          />
          <span className="research-opt-in-label">Share my data with researchers</span>
        </label>
        {researchOptInSaving && <span className="research-opt-in-saving" aria-live="polite">Saving…</span>}
        <Link to="/privacy" className="research-privacy-link">Privacy policy</Link>
      </div>

      <p className="page-intro">
        Only the first section is required. However, if you opted-in above, we encourage you to fill out the entire questionnaire to help contribute to women's health research.
      </p>

      {/* Phase Stepper */}
      <div className="phase-stepper">
        {stepperPhaseIndices.map((phaseIdx, stepperIdx) => {
          const phase = phases[phaseIdx];
          const isActive = phaseIdx === effectivePhase;
          const fullIdx = visiblePhaseIndices.indexOf(phaseIdx);
          const isCompleted = phase?.required_phase
            ? isPhase1Complete(formData)
            : isPhaseComplete(phaseIdx);
          return (
            <div key={phase.id} className="phase-step">
              <button
                type="button"
                className={`phase-dot ${isActive ? 'active' : ''} ${isCompleted ? 'completed' : ''}`}
                onClick={() => {
                  if (currentPhaseData?.required_phase && fullIdx > currentVisibleIdx) {
                    if (!validateRequiredFields()) return;
                  }
                  setCurrentPhase(phaseIdx);
                  window.scrollTo({ top: 0, behavior: 'smooth' });
                }}
                title={phase.title}
              >
                {stepperIdx + 1}
              </button>
              <span className={`phase-label ${isActive ? 'active' : ''}`}>
                {phase.title}
              </span>
            </div>
          );
        })}
      </div>

      <div className="phase-indicator">
        Step {currentVisibleIdx + 1} of {stepperPhaseIndices.length}
        {currentPhaseData?.required_phase ? ' (Required)' : ' (Optional)'}
      </div>

      <div className="card form-card">
        <form onSubmit={(e) => e.preventDefault()}>
          <div className="phase-fields">
            {currentPhaseData && Object.entries(currentPhaseData.fields).map(([fieldName, fieldConfig]) =>
              renderField(fieldName, fieldConfig)
            )}
          </div>

          <div className="form-actions">
            <div className="nav-buttons">
              <button
                type="button"
                className="btn btn-secondary"
                onClick={handlePrev}
                disabled={isFirstVisiblePhase}
              >
                Previous
              </button>
              {!isLastVisiblePhase && (
                <button type="button" className="btn btn-primary" onClick={handleNext}>
                  Next
                </button>
              )}
            </div>
            {autoSaveLabel && (
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                <span style={{ fontSize: '0.8rem', color: autoSaveStatus === 'error' ? '#e03131' : '#868e96' }}>
                  {autoSaveLabel}
                </span>
              </div>
            )}
          </div>
        </form>
        {message && (
          <div className={`message ${message.includes('Error') || message.includes('Please fill') ? 'error' : 'success'}`}>
            {message}
          </div>
        )}
      </div>
    </div>
  );
}

export default Questionnaires;
